import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Plus, ArrowUp, ArrowDown, Trash2, Sparkles, Radio } from "lucide-react";
import { API, inr, errMsg } from "../lib/api";
import Seo from "../components/Seo";

export default function CreatorDashboard() {
  const [d, setD] = useState(null);
  const [products, setProducts] = useState([]);
  const [pid, setPid] = useState("");
  const [live, setLive] = useState({ active: false, title: "", at: "" });
  const load = () => API.get("/creator/dashboard").then((r) => {
    setD(r.data);
    setLive({ active: !!r.data.creator?.live_active, title: r.data.creator?.live_title || "", at: r.data.creator?.live_at || "" });
  }).catch(() => setD(false));
  useEffect(() => { load(); API.get("/products?limit=100").then((r) => setProducts(r.data.items)).catch(() => {}); }, []);
  if (d === false) return <div className="max-w-3xl mx-auto px-4 py-20 text-stone-500">You need an approved creator account to view this.</div>;
  if (!d) return <div className="max-w-3xl mx-auto px-4 py-20 text-stone-500">Loading…</div>;

  const addLink = async () => {
    if (!pid) return;
    try { await API.post("/creators/links", { product_id: pid }); toast.success("Added to your picks"); setPid(""); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const saveLive = async (next) => {
    const payload = { ...live, ...next };
    setLive(payload);
    try { await API.post("/creators/live", payload); toast.success(payload.active ? "You're live in Creator Corner 💗" : "Live session ended"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Link copied 💗"); };
  const move = async (idx, dir) => {
    const links = [...d.links];
    const j = idx + dir;
    if (j < 0 || j >= links.length) return;
    [links[idx], links[j]] = [links[j], links[idx]];
    setD({ ...d, links });
    try { await API.post("/creators/links/reorder", { link_ids: links.map((l) => l.id) }); }
    catch (e) { toast.error(errMsg(e)); load(); }
  };
  const remove = async (lid) => {
    setD({ ...d, links: d.links.filter((l) => l.id !== lid) });
    try { await API.delete(`/creators/links/${lid}`); toast.success("Removed from picks"); }
    catch (e) { toast.error(errMsg(e)); load(); }
  };
  const cards = [["Clicks", d.clicks], ["Orders", d.orders], ["Commission Earned", inr(d.commission_total)], ["Pending", inr(d.commission_pending)]];
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12" data-testid="creator-dashboard">
      <Seo title="Creator Dashboard | Suvikka" noindex />
      <h1 className="font-display text-3xl font-semibold text-[#231518]">Creator Dashboard</h1>
      <p className="text-[#755C62] mt-1">Ref {d.creator.ref} · {d.creator.commission_percent}% commission</p>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        {cards.map(([l, v], i) => <div key={i} className="bg-white border border-[#EADBD8] rounded-2xl p-5 shadow-soft" data-testid={`creator-stat-${i}`}><p className="text-[11px] uppercase tracking-wider text-[#9E838B]">{l}</p><p className="font-display text-2xl font-bold mt-1 text-[#231518]">{v}</p></div>)}
      </div>

      <div className={`rounded-2xl p-5 mt-6 shadow-soft border ${live.active ? "border-[#6A1B29] bg-[#2A1418] text-white" : "border-[#EADBD8] bg-white"}`} data-testid="creator-live-card">
        <div className="flex items-center gap-2">
          <Radio className={`w-5 h-5 ${live.active ? "text-[#F0C97B] animate-pulse" : "text-[#6A1B29]"}`} />
          <h2 className={`font-display text-lg font-semibold ${live.active ? "text-white" : "text-[#231518]"}`}>Host a Live Chat</h2>
        </div>
        <p className={`text-sm mt-1 ${live.active ? "text-white/75" : "text-[#755C62]"}`}>When you go live, a pinned banner invites every Sakhi into <span className="font-semibold">Creator Corner</span> to chat with you.</p>
        <div className="grid sm:grid-cols-2 gap-3 mt-4">
          <input value={live.title} onChange={(e) => setLive({ ...live, title: e.target.value })} placeholder="What's the session about? (e.g. Festive glam Q&A)" data-testid="creator-live-title"
            className={`border rounded-lg px-3 py-2.5 text-sm ${live.active ? "bg-white/10 border-white/20 text-white placeholder:text-white/50" : "border-stone-300"}`} />
          <input value={live.at} onChange={(e) => setLive({ ...live, at: e.target.value })} placeholder="When? (e.g. Today 6 PM)" data-testid="creator-live-at"
            className={`border rounded-lg px-3 py-2.5 text-sm ${live.active ? "bg-white/10 border-white/20 text-white placeholder:text-white/50" : "border-stone-300"}`} />
        </div>
        <div className="flex items-center gap-3 mt-4">
          {live.active ? (
            <button onClick={() => saveLive({ active: false })} data-testid="creator-go-offline" className="bg-white text-[#6A1B29] text-sm font-semibold px-5 py-2.5 rounded-full">End Live Session</button>
          ) : (
            <button onClick={() => saveLive({ active: true })} data-testid="creator-go-live" className="inline-flex items-center gap-1.5 bg-[#6A1B29] hover:bg-[#501325] text-white text-sm font-semibold px-5 py-2.5 rounded-full"><Radio className="w-4 h-4" /> Go Live</button>
          )}
          {live.active && <span className="text-xs font-semibold text-[#F0C97B]">You are live now</span>}
        </div>
      </div>

      <div className="bg-white border border-[#EADBD8] rounded-2xl p-5 mt-6 shadow-soft">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-[#6A1B29]" />
          <h2 className="font-display text-lg font-semibold text-[#231518]">Your Spotlight Picks</h2>
        </div>
        <p className="text-sm text-[#755C62] mt-1">Curate and reorder the products shown on your public creator card. The <span className="font-semibold text-[#6A1B29]">first 4</span> appear in the Creator Spotlight.</p>
        <div className="flex gap-2 mt-4">
          <select value={pid} onChange={(e) => setPid(e.target.value)} className="flex-1 border border-stone-300 rounded-lg px-3 py-2.5 text-sm" data-testid="creator-link-product"><option value="">Add a product to your picks…</option>{products.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}</select>
          <button onClick={addLink} className="inline-flex items-center gap-1.5 bg-[#6A1B29] hover:bg-[#501325] text-white text-sm font-semibold px-4 py-2.5 rounded-lg" data-testid="creator-add-link"><Plus className="w-4 h-4" /> Add</button>
        </div>
        <div className="mt-4 space-y-2">
          {d.links.map((lk, idx) => (
            <div key={lk.id} className={`flex items-center gap-3 border rounded-xl p-3 ${idx < 4 ? "border-[#EADBD8] bg-[#FFF7FA]" : "border-stone-100"}`} data-testid={`creator-link-${lk.id}`}>
              {lk.image && <img src={lk.image} alt={lk.title} className="w-10 h-10 rounded-lg object-cover" />}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate text-[#231518]">{lk.title} {idx < 4 && <span className="text-[10px] font-semibold text-[#6A1B29] bg-[#F9ECE8] px-1.5 py-0.5 rounded-full ml-1">In spotlight</span>}</p>
                <p className="text-xs text-stone-400 truncate">{lk.clicks} clicks · {lk.url}</p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => move(idx, -1)} disabled={idx === 0} className="p-1.5 text-stone-400 hover:text-[#6A1B29] disabled:opacity-30" data-testid={`link-up-${lk.id}`}><ArrowUp className="w-4 h-4" /></button>
                <button onClick={() => move(idx, 1)} disabled={idx === d.links.length - 1} className="p-1.5 text-stone-400 hover:text-[#6A1B29] disabled:opacity-30" data-testid={`link-down-${lk.id}`}><ArrowDown className="w-4 h-4" /></button>
                <button onClick={() => copy(lk.url)} className="p-1.5 text-stone-400 hover:text-[#6A1B29]" data-testid={`link-copy-${lk.id}`}><Copy className="w-4 h-4" /></button>
                <button onClick={() => remove(lk.id)} className="p-1.5 text-stone-400 hover:text-red-500" data-testid={`link-remove-${lk.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
          {d.links.length === 0 && <p className="text-sm text-stone-400">No picks yet. Add products above to build your spotlight.</p>}
        </div>
      </div>
    </div>
  );
}
